import React from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  useTheme,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import PageContainer from "../../components/PageContainer";
import PageSection from "../../components/PageSection";
import { knowledgeItems } from "../../data/products";
import { lightTheme } from "../../styles/theme";

const NoticeboardHelp: React.FC = () => {
  const theme = useTheme();
  const noticeboardData = knowledgeItems.find(
    (item) => item.id === "noticeboard"
  );

  if (!noticeboardData) {
    return (
      <PageContainer>
        <Typography>Help content not found</Typography>
      </PageContainer>
    );
  }

  const renderContent = (content: any): React.ReactElement | null => {
    if (!content) return null;

    if (typeof content === "string") {
      return (
        <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
          {content}
        </Typography>
      );
    }

    if (Array.isArray(content)) {
      return (
        <List dense sx={{ mb: 2 }}>
          {content.map((item, index) => (
            <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={typeof item === "string" ? item : JSON.stringify(item)}
                sx={{ "& .MuiListItemText-primary": { fontSize: (theme) => theme.typography.body2.fontSize } }}
              />
            </ListItem>
          ))}
        </List>
      );
    }

    if (typeof content === "object" && content !== null) {
      return (
        <Box>
          {Object.entries(content).map(([key, value]) => {
            if (!value || value === "") return null;

            const displayKey = key
              .replace(/([A-Z])/g, " $1")
              .replace(/^./, (str) => str.toUpperCase())
              .trim();

            return (
              <Box key={key} sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{
                    mb: 1.5,
                    color: "primary.main",
                    fontSize: "1rem",
                  }}
                >
                  {displayKey}
                </Typography>
                {renderContent(value)}
              </Box>
            );
          })}
        </Box>
      );
    }

    return null;
  };

  return (
    <PageContainer>
      {/* Header */}
      <PageSection sx={{ mb: 4 }}>

        <Paper
          elevation={0}
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${lightTheme.colors.primaryAltDark} 100%)`,
            borderRadius: 3,
            p: { xs: 4, md: 6 },
            color: "common.white",
            textAlign: "center",
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
          >
            {noticeboardData.title}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              opacity: 0.9,
              fontSize: { xs: "1rem", md: "1.125rem" },
              maxWidth: "800px",
              mx: "auto",
              lineHeight: 1.6,
              fontWeight: 400,
            }}
          >
            {noticeboardData.description}
          </Typography>

          {/* Tags */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              justifyContent: "center",
              mt: 3,
            }}
          >
            {noticeboardData.tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                variant="outlined"
                sx={{
                  color: "common.white",
                  borderColor: theme.palette.whiteOverlays.overlay30,
                  "&:hover": {
                    borderColor: "white",
                  },
                }}
              />
            ))}
          </Box>
        </Paper>
      </PageSection>

      {/* Content */}
      <PageSection>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            bgcolor: "card",
          }}
        >
          {renderContent(noticeboardData.content)}
        </Paper>
      </PageSection>
    </PageContainer>
  );
};

export default NoticeboardHelp;
